import { createMailGeneratedMenuItem } from '../../util/createMailRibbonButton';
import { createAppButton } from 'owa-command-ribbon/lib/util/createAppButton';
import loc from 'owa-localize';
import Strings from 'owa-locstrings/lib/strings/ribbon_mail_buttons_and_tabs.locstring.json';
import { insertSignature } from 'owa-mail-compose-command';
import { getKeytipString, constructKeytip } from 'owa-command-ribbon';
import { getComposeRibbonId } from 'owa-compose-ribbon-utils/lib/utils/getComposeRibbonId';
import { getComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';
import { getSignatureStore } from 'owa-mail-signature/lib/store/signatureStore';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import isRoamingSignatureEnabled from 'owa-roaming-signature-option/lib/utils/isRoamingSignatureEnabled';
import { MailRibbonTabId, MailRibbonControlId } from 'owa-ribbon-ids';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { MenuItemControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/controls/Menu';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import { getSelectedSourceId } from 'owa-account-source-list';

export default function getSignatureSections(
    keytipTabId: MailRibbonTabId | undefined,
    popoutComposeViewState?: ComposeViewState
): MenuSection[] {
    const sections: MenuSection[] = [];

    sections.push({ controls: getSignatureControls(popoutComposeViewState) });
    sections.push({ controls: getSignatureOptionsControls(keytipTabId) });

    return sections;
}

function getSignatureOptionsControls(
    keytipTabId: MailRibbonTabId | undefined
): MenuItemControlProps[] {
    const controls: MenuItemControlProps[] = [];

    controls.push(
        createAppButton(
            MailRibbonControlId.SignatureOptions,
            loc(Strings.signatureOptions_Button),
            () => {
                lazyMountAndShowFullOptions.importAndExecute('mail', 'messageContent');
            },
            constructKeytip(
                keytipTabId
                    ? [getKeytipString(keytipTabId), getKeytipString(MailRibbonControlId.AttachSig)]
                    : [getKeytipString(MailRibbonControlId.AttachSig)],
                getKeytipString(MailRibbonControlId.SignatureOptions)
            ),
            undefined /* icon */,
            undefined /* iconColor */,
            getDefaultRibbonStyles()
        )
    );

    return controls;
}

function getSignatureControls(popoutComposeViewState?: ComposeViewState): MenuItemControlProps[] {
    const controls: MenuItemControlProps[] = [];
    const viewState: ComposeViewState | undefined = popoutComposeViewState ?? getComposeViewState();

    if (viewState === undefined) {
        return controls;
    }

    if (isRoamingSignatureEnabled()) {
        const { roamingSignatureMap } = getSignatureStore(getSelectedSourceId());

        roamingSignatureMap.forEach(signature => {
            let signatureName: string | undefined = undefined;

            if (signature.html) {
                signatureName = signature.html.name;
            } else if (signature.txt) {
                signatureName = signature.txt.name;
            } else {
                return;
            }

            if (signatureName) {
                controls.push(
                    createMailGeneratedMenuItem(
                        getComposeRibbonId('sig_' + signatureName, popoutComposeViewState),
                        signatureName,
                        undefined /*tooltip*/,
                        () => {
                            insertSignature(
                                popoutComposeViewState ?? getComposeViewState()!,
                                signature,
                                true /*replaceExisting*/
                            );
                        }
                    )
                );
            }
        });
    }

    if (controls.length == 0) {
        // Use default signature
        const userOptions = getUserConfiguration().UserOptions;
        const { bodyType } = viewState;
        const signatureName: string =
            (bodyType == 'HTML' ? userOptions?.SignatureHtml : userOptions?.SignatureText) || '';

        if (signatureName) {
            controls.push(
                createMailGeneratedMenuItem(
                    getComposeRibbonId('sig_Default', popoutComposeViewState),
                    loc(Strings.attachSig_Button),
                    undefined /*tooltip*/,
                    () => {
                        insertSignature(
                            popoutComposeViewState ?? getComposeViewState()!,
                            signatureName,
                            true /*replaceExisting*/
                        );
                    }
                )
            );
        }
    }

    return controls;
}
